<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import IconStarOutline from '@/shared/components/UI/Icons/IconStarOutline.vue';
import IconStarSolid from '@/shared/components/UI/Icons/IconStarSolid.vue';
import { useTranslations } from '@/shared/composables/useTranslations';

const { t } = useTranslations();

const hoveredStar = ref<number>(0);
const { initialRating = 0, disabled = false } = defineProps<{
  initialRating?: number;
  disabled?: boolean;
}>();

const onStarHover = (index: number) => {
  hoveredStar.value = index;
};

const onStarLeave = () => {
  hoveredStar.value = initialRating;
};

onMounted(() => {
  hoveredStar.value = initialRating;
});

const reviewStatusText = computed(() => {
  switch (hoveredStar.value) {
    case 1:
      return t('reviews.levels.1_stars');
    case 2:
      return t('reviews.levels.2_stars');
    case 3:
      return t('reviews.levels.3_stars');
    case 4:
      return t('reviews.levels.4_stars');
    case 5:
      return t('reviews.levels.5_stars');
    default:
      return t('reviews.levels.0_stars');
  }
});

defineEmits(['click']);
</script>

<template>
  <div
    data-testid="main-container"
    class="group flex w-full flex-col items-center md:items-start"
    @mouseleave="hoveredStar = initialRating"
  >
    <div
      class="inline-grid max-w-80 grid-cols-5 items-center gap-1.5 group-[.hb-centered]:mx-auto"
    >
      <div
        v-for="index in 5"
        :key="index"
        class="group size-full"
        :class="{
          'hb-active': index <= hoveredStar,
          'cursor-pointer': !disabled
        }"
        data-testid="hoverable-star"
        @mouseover="!disabled && onStarHover(index)"
        @mouseleave="!disabled && onStarLeave"
        @click.prevent="() => $emit('click', index)"
      >
        <div
          class="grid aspect-square size-full min-w-12 place-content-center rounded-md border border-primary transition-all duration-150 sm:min-w-14"
          :class="index <= hoveredStar ? 'bg-primary' : ''"
        >
          <div
            class="size-full transition-transform duration-700"
            :class="index <= hoveredStar ? 'scale-150' : 'scale-100'"
          >
            <div
              class="text-lg ease-in-out [transition:transform_0.4s,colors_0.2s]"
              :class="[
                {
                  'group-hover:-rotate-6': index === 5 && index <= hoveredStar
                },
                index <= hoveredStar
                  ? 'text-white group-hover:rotate-[72deg] group-[.hb-active]:rotate-[72deg]'
                  : 'text-primary'
              ]"
            >
              <IconStarSolid v-if="index <= hoveredStar" />
              <IconStarOutline v-else />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="mt-2 text-center text-sm group-[.hb-centered]:mx-auto md:text-left"
    >
      <template v-if="hoveredStar">
        {{ t('reviews.x_out_of_5_stars', { number_of_stars: hoveredStar }) }}
        <strong class="text-primary">{{ reviewStatusText }}</strong>
      </template>
      <template v-else>
        {{ t('reviews.levels.0_stars') }}
      </template>
    </div>
  </div>
</template>
